import React, { useState, useEffect } from 'react';
import { buildsApi } from '../../../api/builds';
import type { PdbFunction, DisassemblyLine } from '../../../api/builds';
import {
  X,
  Loader,
  Copy,
  Check,
  AlertCircle,
  Code,
  Cpu,
  Hash,
} from 'lucide-react';
import { clsx } from 'clsx';

interface DisassemblySidebarProps {
  isOpen: boolean;
  onClose: () => void;
  uploadId: string;
  func: PdbFunction | null;
}

// Instruction categories for syntax highlighting
const JUMP_INSTRUCTIONS = ['jmp', 'je', 'jne', 'jz', 'jnz', 'jg', 'jge', 'jl', 'jle', 'ja', 'jae', 'jb', 'jbe', 'jc', 'jnc', 'jo', 'jno', 'js', 'jns', 'jp', 'jnp', 'loop', 'loope', 'loopne'];
const CALL_INSTRUCTIONS = ['call', 'ret', 'retn', 'retf'];
const STACK_INSTRUCTIONS = ['push', 'pop', 'pushf', 'pushfd', 'popf', 'popfd', 'pusha', 'pushad', 'popa', 'popad'];
const MOVE_INSTRUCTIONS = ['mov', 'movzx', 'movsx', 'lea', 'xchg', 'cmov', 'cmove', 'cmovne', 'cmovz', 'cmovnz'];
const ARITHMETIC_INSTRUCTIONS = ['add', 'sub', 'mul', 'imul', 'div', 'idiv', 'inc', 'dec', 'neg', 'adc', 'sbb'];
const LOGIC_INSTRUCTIONS = ['and', 'or', 'xor', 'not', 'test', 'cmp', 'shl', 'shr', 'sal', 'sar', 'rol', 'ror'];

function getMnemonicClass(mnemonic: string): string {
  const lower = mnemonic.toLowerCase();
  
  if (JUMP_INSTRUCTIONS.includes(lower)) {
    return 'text-yellow-400';
  }
  if (CALL_INSTRUCTIONS.includes(lower)) {
    return 'text-cyan-400';
  }
  if (STACK_INSTRUCTIONS.includes(lower)) {
    return 'text-purple-400';
  }
  if (MOVE_INSTRUCTIONS.some(m => lower.startsWith(m))) {
    return 'text-emerald-400';
  }
  if (ARITHMETIC_INSTRUCTIONS.includes(lower)) {
    return 'text-blue-400';
  }
  if (LOGIC_INSTRUCTIONS.some(l => lower.startsWith(l))) {
    return 'text-pink-400';
  }
  if (lower === 'nop') {
    return 'text-slate-500';
  }
  if (lower === 'int3') {
    return 'text-red-400';
  }
  
  return 'text-slate-300';
}

function formatOperands(operands: string): React.ReactNode {
  // Highlight registers, numbers, and memory references
  const parts = operands.split(/(\[.*?\]|0x[0-9A-Fa-f]+|\b(?:eax|ebx|ecx|edx|esi|edi|esp|ebp|rax|rbx|rcx|rdx|rsi|rdi|rsp|rbp|r8|r9|r10|r11|r12|r13|r14|r15|al|bl|cl|dl|ah|bh|ch|dh|ax|bx|cx|dx|si|di|sp|bp)\b)/gi);
  
  return parts.map((part, i) => {
    // Memory reference
    if (part.startsWith('[') && part.endsWith(']')) {
      return (
        <span key={i} className="text-orange-400">{part}</span>
      );
    }
    // Hex number
    if (/^0x[0-9A-Fa-f]+$/i.test(part)) {
      return (
        <span key={i} className="text-cyan-300">{part}</span>
      );
    }
    // Register
    if (/^(eax|ebx|ecx|edx|esi|edi|esp|ebp|rax|rbx|rcx|rdx|rsi|rdi|rsp|rbp|r8|r9|r10|r11|r12|r13|r14|r15|al|bl|cl|dl|ah|bh|ch|dh|ax|bx|cx|dx|si|di|sp|bp)$/i.test(part)) {
      return (
        <span key={i} className="text-amber-400">{part}</span>
      );
    }
    
    return <span key={i}>{part}</span>;
  });
}

export function DisassemblySidebar({
  isOpen,
  onClose,
  uploadId,
  func,
}: DisassemblySidebarProps) {
  const [instructions, setInstructions] = useState<DisassemblyLine[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Fetch disassembly when function changes
  useEffect(() => {
    if (!func || !isOpen) {
      setInstructions([]);
      return;
    }

    const fetchDisassembly = async () => {
      setLoading(true);
      setError(null);

      try {
        const result = await buildsApi.disassemble(uploadId, func.rva, func.size);
        setInstructions(result.instructions);
      } catch (err: unknown) {
        const axiosError = err as { response?: { data?: { error?: string } } };
        setError(axiosError.response?.data?.error || 'Failed to disassemble function');
      } finally {
        setLoading(false);
      }
    };

    fetchDisassembly();
  }, [func, uploadId, isOpen]);

  // Copy disassembly to clipboard
  const copyToClipboard = () => {
    const text = instructions
      .map(i => `${i.address}  ${i.bytes.padEnd(24)}  ${i.text}`)
      .join('\n');
    
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Format hex address
  const formatHex = (value: number): string => {
    return '0x' + value.toString(16).toUpperCase().padStart(8, '0');
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={clsx(
          'fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity duration-300',
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        onClick={onClose}
      />

      {/* Sidebar */}
      <div
        className={clsx(
          'fixed top-0 right-0 h-full w-full sm:w-[500px] bg-slate-900 border-l border-slate-700/50 z-50 transform transition-transform duration-300 flex flex-col',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-slate-700/50">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/20 to-purple-500/20 flex items-center justify-center border border-cyan-500/20 flex-shrink-0">
              <Code className="w-5 h-5 text-cyan-400" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-white truncate">
                {func?.demangledName || func?.name || 'Disassembly'}
              </h2>
              {func && (
                <p className="text-sm text-slate-400 font-mono">
                  {formatHex(func.rva)} {func.size ? `(${func.size} bytes)` : ''}
                </p>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={copyToClipboard}
              disabled={instructions.length === 0}
              className={clsx(
                'p-2 rounded-lg transition-colors',
                instructions.length === 0
                  ? 'text-slate-600 cursor-not-allowed'
                  : copied
                  ? 'text-emerald-400 bg-emerald-500/10'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              )}
              title={copied ? 'Copied!' : 'Copy to clipboard'}
            >
              {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Loader className="w-8 h-8 text-cyan-400 animate-spin mx-auto mb-4" />
                <p className="text-slate-400">Disassembling...</p>
              </div>
            </div>
          ) : error ? (
            <div className="p-6">
              <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <div>
                    <p className="text-red-400 font-medium">Disassembly Failed</p>
                    <p className="text-red-400/70 text-sm">{error}</p>
                  </div>
                </div>
              </div>
            </div>
          ) : instructions.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-slate-500">
                <Cpu className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No instructions to display</p>
              </div>
            </div>
          ) : (
            <div className="h-full overflow-auto">
              {/* Legend */}
              <div className="sticky top-0 bg-slate-900/95 backdrop-blur-sm border-b border-slate-700/50 px-6 py-2">
                <div className="flex flex-wrap gap-3 text-xs">
                  <span className="text-yellow-400">● jumps</span>
                  <span className="text-cyan-400">● calls</span>
                  <span className="text-purple-400">● stack</span>
                  <span className="text-emerald-400">● moves</span>
                  <span className="text-blue-400">● arithmetic</span>
                  <span className="text-pink-400">● logic</span>
                </div>
              </div>

              {/* Instructions */}
              <div className="font-mono text-xs leading-relaxed">
                {instructions.map((instr, idx) => (
                  <div
                    key={idx}
                    className="flex gap-4 px-6 py-1.5 hover:bg-slate-800/30 group"
                  >
                    {/* Address */}
                    <span className="text-slate-500 w-24 flex-shrink-0">
                      {instr.address}
                    </span>
                    
                    {/* Bytes */}
                    <span className="text-slate-600 w-28 flex-shrink-0 truncate group-hover:text-slate-500" title={instr.bytes}>
                      {instr.bytes}
                    </span>
                    
                    {/* Mnemonic */}
                    <span className={clsx('w-12 flex-shrink-0', getMnemonicClass(instr.mnemonic))}>
                      {instr.mnemonic}
                    </span>
                    
                    {/* Operands */}
                    <span className="text-slate-300 flex-1 truncate" title={instr.operands}>
                      {formatOperands(instr.operands)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer with stats */}
        {instructions.length > 0 && (
          <div className="flex-shrink-0 px-6 py-3 border-t border-slate-700/50 bg-slate-800/30">
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span className="flex items-center gap-2">
                <Hash className="w-3.5 h-3.5" />
                {instructions.length} instructions
              </span>
              <span>
                Intel syntax
              </span>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

